import React, { useState, useEffect, useContext } from "react";
import { 
  CreditCard, User, Image as ImageIcon, CheckCircle, Clock, XCircle, 
  Send, RefreshCw, Filter, Trash2, ShieldCheck, Search 
} from "lucide-react";
import { employeeWorkforceAPI, customerAPI } from "../../api/apiService";
import { NotificationContext } from "../../context/NotificationContext";
import EmptyState from "../../components/EmptyState";

export default function CollectionsPage() {
  const { addNotification } = useContext(NotificationContext);
  const [collections, setCollections] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Form State
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [amountCollected, setAmountCollected] = useState("");
  const [paymentMode, setPaymentMode] = useState("UPI");
  const [proofBase64, setProofBase64] = useState("");
  const [fileName, setFileName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchCollectionsAndCustomers = async () => {
    setLoading(true);
    try {
      const [colRes, custRes] = await Promise.all([
        employeeWorkforceAPI.getCollections(),
        customerAPI.getAllCustomers()
      ]);
      setCollections(colRes.data);
      setCustomers(custRes.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to load collections records", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollectionsAndCustomers();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        // base64 format string
        setProofBase64(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCollectionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomerId) {
      addNotification("Please select a customer.", "warning");
      return;
    }
    if (!amountCollected || parseFloat(amountCollected) <= 0) {
      addNotification("Please enter a valid amount.", "warning");
      return;
    }
    if (!proofBase64) {
      addNotification("Please upload payment receipt screenshot proof.", "warning");
      return;
    }

    setSubmitLoading(true);
    try {
      const selectedCust = customers.find(c => String(c.id) === String(selectedCustomerId));
      const customerNameStr = selectedCust 
        ? `${selectedCust.first_name} ${selectedCust.last_name || ""}`.trim() 
        : "Unknown Customer";

      const payload = {
        customer_name: customerNameStr,
        amount_collected: amountCollected,
        payment_mode: paymentMode,
        proof_screenshot: proofBase64
      };
      await employeeWorkforceAPI.createCollection(payload);
      addNotification("Collection log submitted successfully!", "success");
      
      // Clear form
      setSelectedCustomerId("");
      setAmountCollected("");
      setPaymentMode("UPI");
      setProofBase64("");
      setFileName("");
      
      // Refresh list
      const colRes = await employeeWorkforceAPI.getCollections();
      setCollections(colRes.data);
    } catch (err) {
      console.error(err);
      addNotification(err.response?.data?.detail || "Failed to submit collection.", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <EmptyState
          preset="verification"
          title="Loading Collections..."
          message="Please wait a moment."
        />
      </div>
    );
  }

  const statusBadges = {
    "PENDING": "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800",
    "VERIFIED": "bg-green-50 text-green-600 dark:bg-green-950/30 dark:text-green-400 border border-green-200 dark:border-green-800",
    "REJECTED": "bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400 border border-red-200 dark:border-red-800",
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="bg-white dark:bg-slate-800 p-6 md:p-8 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-600 rounded-2xl text-white shadow-lg shadow-green-500/20">
              <CreditCard className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">Track & Log Collections</h1>
              <p className="text-gray-700 dark:text-slate-300 font-medium">Record customer payments, deposit proofs, and track verification status</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Submit Collection Form */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm space-y-5">
            <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider mb-2">
              Log Received Payment
            </h2>
            
            <form onSubmit={handleCollectionSubmit} className="space-y-4">
              {/* Select Customer */}
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase tracking-widest mb-2">
                  Customer
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition dark:text-white appearance-none"
                    value={selectedCustomerId}
                    onChange={(e) => setSelectedCustomerId(e.target.value)}
                    required
                  >
                    <option value="">Choose Customer...</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.first_name} {c.last_name || ""} (+91 {c.phone})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Amount Collected */}
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase tracking-widest mb-2">
                  Amount Collected (₹)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-gray-400">₹</span>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Enter amount"
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition dark:text-white"
                    value={amountCollected}
                    onChange={(e) => setAmountCollected(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Payment Mode */}
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase tracking-widest mb-2">
                  Payment Mode
                </label>
                <select
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition dark:text-white appearance-none"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  required
                >
                  <option value="UPI">UPI / GPay / PhonePe</option>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Debit/Credit Card</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                </select>
              </div>

              {/* Screenshot Proof */}
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase tracking-widest mb-2">
                  Payment Receipt Screenshot
                </label>
                <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-2xl p-4 bg-gray-50 dark:bg-slate-900 transition hover:bg-gray-100/50">
                  <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-xs text-gray-400 font-medium mb-2">
                    {fileName ? fileName : "Upload screenshot file (JPG, PNG)"}
                  </span>
                  <label className="cursor-pointer bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs font-bold px-4 py-2 rounded-xl shadow-xs hover:shadow transition">
                    Browse File
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
                {proofBase64 && (
                  <div className="mt-3 relative w-full h-32 rounded-xl overflow-hidden border border-gray-100">
                    <img src={proofBase64} alt="Preview proof" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => { setProofBase64(""); setFileName(""); }}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 shadow-sm"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitLoading}
                className="w-full py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-bold flex items-center justify-center gap-2 transition active:scale-95 shadow-md shadow-green-500/10"
              >
                {submitLoading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Log Entry
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Collection Log List */}
          <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col justify-between">
            <div>
              <div className="p-6 border-b border-gray-50 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider">
                  Logged Collections
                </h2>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search customer..."
                    className="pl-9 pr-4 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-xl text-xs font-medium focus:ring-1 focus:ring-green-500 transition dark:text-white"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-700">
                    <tr>
                      <th className="px-6 py-4 text-xs font-black uppercase text-gray-600 dark:text-slate-400 tracking-wider">Customer</th>
                      <th className="px-6 py-4 text-xs font-black uppercase text-gray-600 dark:text-slate-400 tracking-wider">Amount</th>
                      <th className="px-6 py-4 text-xs font-black uppercase text-gray-600 dark:text-slate-400 tracking-wider">Mode</th>
                      <th className="px-6 py-4 text-xs font-black uppercase text-gray-600 dark:text-slate-400 tracking-wider">Status</th>
                      <th className="px-6 py-4 text-xs font-black uppercase text-gray-600 dark:text-slate-400 tracking-wider">Date</th>
                      <th className="px-6 py-4 text-xs font-black uppercase text-gray-600 dark:text-slate-400 tracking-wider text-right">Proof</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-700 text-sm">
                    {collections.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                          No collection entries recorded.
                        </td>
                      </tr>
                    ) : collections
                      .filter(c => 
                        (c.customer_name || "")
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase())
                      )
                      .map(item => (
                        <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/30 transition-colors">
                          <td className="px-6 py-4">
                            <p className="font-bold text-gray-900 dark:text-white">
                              {item.customer_name}
                            </p>
                          </td>
                          <td className="px-6 py-4 font-black text-gray-900 dark:text-white">
                            ₹{parseFloat(item.amount_collected).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 font-bold text-xs uppercase tracking-widest text-gray-700 dark:text-slate-300">
                            {item.payment_mode}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${statusBadges[item.status]}`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-xs text-gray-600 dark:text-slate-400 font-medium">
                            {new Date(item.date_collected).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                          </td>
                          <td className="px-6 py-4 text-right">
                            {item.proof_screenshot ? (
                              <div className="relative group inline-block">
                                <img
                                  src={item.proof_screenshot}
                                  alt="Receipt proof"
                                  className="w-8 h-8 rounded-lg object-cover cursor-pointer border border-gray-200 transition group-hover:scale-150 group-hover:rotate-3 shadow-sm"
                                />
                                {/* Hover Modal tooltip */}
                                <div className="absolute right-10 bottom-0 scale-0 group-hover:scale-100 transition-all duration-200 z-50 bg-slate-900 p-2 rounded-xl shadow-2xl border border-slate-700 w-48 h-48 pointer-events-none">
                                  <img src={item.proof_screenshot} alt="Receipt proof expanded" className="w-full h-full object-contain" />
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">No Proof</span>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
